import { useEffect, useRef } from 'react';

import { VOUCHER_REWARD_TYPE_OPTIONS } from '../constants';
import {
  DEFAULT_REWARD_CONFIGURATION,
  EMPTY_PRODUCT_OPTION,
} from '../constants/voucher-product-form.constants';
import type {
  PurchaseProductOption,
  RewardConfiguration,
  RewardType,
  RewardValueField,
  VoucherFormClearErrors,
  VoucherFormSetValue,
} from '../types/voucher-product-form.types';
import { mapOptionToRewardType } from '../utils/voucher-product-form.utils';

export const useInitializeRewardConfiguration = (
  rewardConfiguration: RewardConfiguration | undefined,
  setValue: VoucherFormSetValue
) => {
  useEffect(() => {
    if (!rewardConfiguration) {
      setValue('reward_configuration', { ...DEFAULT_REWARD_CONFIGURATION });
    }
  }, [rewardConfiguration, setValue]);
};

export const useEnsurePurchaseProductField = (
  append: (
    value: PurchaseProductOption,
    options?: { shouldFocus?: boolean }
  ) => void,
  hasFields: boolean
) => {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current && !hasFields) {
      append({ ...EMPTY_PRODUCT_OPTION }, { shouldFocus: false });
      initializedRef.current = true;
    }
  }, [append, hasFields]);
};

/**
 * Hook to clear free product when reward type is not FREE_GOODS.
 * Auto-assignment is now centralized in the component's useEffect.
 */
export const useAutoAssignFreeProduct = ({
  rewardType,
  currentValue,
  setValue,
  clearErrors,
}: {
  rewardType: RewardType;
  purchaseProducts: PurchaseProductOption[]; // Kept for interface compatibility
  currentValue: PurchaseProductOption | null | undefined;
  setValue: VoucherFormSetValue;
  clearErrors: VoucherFormClearErrors;
  manuallyCleared: boolean; // Kept for interface compatibility
}) => {
  useEffect(() => {
    // Only clear when reward type doesn't match - auto-assign is handled elsewhere
    if (rewardType !== 'FREE_GOODS' && currentValue?.value) {
      setValue('rewardFreeProduct', null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      clearErrors('rewardFreeProduct');
    }
  }, [rewardType, currentValue, setValue, clearErrors]);
};

export const useRewardTypeChangeHandler = ({
  setValue,
  clearErrors,
}: {
  setValue: VoucherFormSetValue;
  clearErrors: VoucherFormClearErrors;
}) => {
  const resetField = (field: RewardValueField) => {
    const path = `reward_configuration.${field}` as const;
    setValue(path, null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors(path);
  };

  return (index: number) => {
    const option = VOUCHER_REWARD_TYPE_OPTIONS[index];
    if (!option) return;

    const nextType = mapOptionToRewardType(option.value);

    setValue('reward_configuration.reward_type', nextType, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (nextType !== 'DISCOUNT_AMOUNT') {
      resetField('discount_amount_value');
    }
    if (nextType !== 'DISCOUNT_PERCENT') {
      resetField('discount_percentage_value');
    }
    if (nextType !== 'FREE_GOODS') {
      resetField('free_goods_quantity_value');
    }

    // For FREE_GOODS, enforce quantity limits to 1
    if (nextType === 'FREE_GOODS') {
      setValue('reward_configuration.min_total_quantity_all_item', 1, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('reward_configuration.free_goods_quantity_value', 1, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    // Note: Reward product auto-assignment is handled by useEffect in the component
    // This ensures state consistency after React has fully processed the type change
  };
};

/**
 * Hook to manually sync reward products when purchase products change.
 * Only syncs FREE_GOODS reward product (Buy A Get A logic).
 * Discount types allow independent product selection.
 */
export const useSyncRewardProducts = ({
  rewardType,
  setValue,
  clearErrors,
}: {
  rewardType: RewardType;
  setValue: VoucherFormSetValue;
  clearErrors: VoucherFormClearErrors;
}) => {
  const syncRewardProducts = (
    purchaseProducts: PurchaseProductOption[],
    currentFreeProduct: PurchaseProductOption | null | undefined
  ) => {
    if (rewardType !== 'FREE_GOODS') return;

    const validPurchaseProducts = purchaseProducts.filter(
      (product) => product?.value && product.value !== ''
    );

    if (validPurchaseProducts.length === 0) return;

    const firstProduct = validPurchaseProducts[0];

    if (!currentFreeProduct?.value) {
      setValue('rewardFreeProduct', firstProduct, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      clearErrors('rewardFreeProduct');
    }
  };

  return { syncRewardProducts };
};
